import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ServiceSelect } from "@boundary/widgets/serviceSelect";
import { Page } from "@boundary/layouts/pageLayout";
import { Spinner } from "@boundary/components/spinner";
import { useOrderFormState } from "@boundary/_hooks/useOrderFormState";
import { ServiceMetadataStore } from "@infrastructure/stores/serviceMetadata.store";
import { ServiceMetadataService } from "@domain/services/serviceMetadata.service";
import { ServiceType } from "@domain/enums/machine.enums";
import { getInfoByService } from "@domain/enums/local/getInfoByService";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import styles from "./serviceVariants.module.scss";

export function ServiceVariantsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const machineIdParam = searchParams.get("machineId");
  const parentParam = searchParams.get("parent");
  const machineId = machineIdParam ? Number(machineIdParam) : 0;
  const parentServiceType = parentParam as ServiceType | null;

  const vm = useOrderFormState(machineId);
  const [variants, setVariants] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!parentServiceType) {
      prefetchAndNavigate(navigate, `/catalog?machineId=${machineId}`);
      return;
    }

    const load = async () => {
      setLoading(true);
      const items = await ServiceMetadataService.loadVariants(parentServiceType);
      setVariants(items as ServiceType[]);
      setLoading(false);
    };

    void load();
  }, [parentServiceType, machineId, navigate]);

  const handleVariantClick = useCallback(
    (serviceType: ServiceType) => {
      const existingService = vm.services.find((s) => s.type === serviceType);

      if (existingService) {
        vm.editServiceFromCart(existingService.id);
      } else {
        vm.createService(serviceType);
      }

      prefetchAndNavigate(navigate, `/service?machineId=${machineId}&serviceType=${serviceType}`);
    },
    [vm, navigate, machineId]
  );

  const handleBackClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      prefetchAndNavigate(navigate, `/catalog?machineId=${machineId}`);
    },
    [navigate, machineId]
  );

  const parentInfo = parentServiceType ? getInfoByService(parentServiceType) : null;

  if (!parentServiceType) {
    return null;
  }

  return (
    <Page>
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header
          back
          onBackClick={handleBackClick}
          center={
            <h1 className={styles.headerTitle}>
              {parentInfo?.title ?? parentServiceType}
            </h1>
          }
        />
        <Page.DisplayZone.Body className={styles.content} padded={false}>
          {loading ? (
            <div className={styles.spinnerContainer}>
              <Spinner />
            </div>
          ) : (
            <div className={styles.serviceList}>
              <ServiceSelect
                items={variants}
                allowedServices={vm.supportedServiceTypes.length > 0 ? vm.supportedServiceTypes : variants}
                isDisabled={(serviceType) => !getInfoByService(serviceType).enabled}
                onClick={handleVariantClick}
              />
            </div>
          )}
        </Page.DisplayZone.Body>
      </Page.DisplayZone>
    </Page>
  );
}
